import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import HeroLoop from "../../Components/Track/HeroLoop";
import StationTrack from "../../Components/Track/StationTrack";
import VerticalTrack from "../../Components/Track/VerticalTrack";
import Marquee from "../../Components/Track/Marquee";
import { useStackedSections } from "../../hooks/useStackedSections";
import LogoMark from "../../Components/Brand/LogoMark";
import LoopWatermark from "../../Components/Brand/LoopWatermark";
import Seo from "../../seo/Seo";
import { organizationSchema, websiteSchema } from "../../seo/site";
import { ArrowIcon } from "../../Components/Brand/Icons";
import { SERVICES } from "../../content/services";
import { CLIENTS, INDUSTRY_LINE, TESTIMONIALS } from "../../content/clients";
import { TECH_MARQUEE } from "../../content/tech";
import HomeContact from "./HomeContact";
import "./home.css";

const PROMISES = [
  { title: "Transparent from day one", text: "Clear scope, regular updates, no surprises." },
  { title: "Planned before it’s built", text: "Scope, architecture and timeline agreed up front." },
  { title: "Fast, without shortcuts", text: "Visible progress, tested before it ships." },
  { title: "Built around you", text: "No templates. Every solution fits your business." },
];

const WHY_US = [
  {
    title: "One accountable team",
    text: "Strategy, design, engineering and growth under one roof. No juggling vendors, no finger-pointing.",
  },
  {
    title: "Total transparency",
    text: "Regular demos and honest updates. You always know where your project stands.",
  },
  {
    title: "Quality built in",
    text: "Testing runs alongside development, not after it. What ships, works.",
  },
  {
    title: "Here after launch",
    text: "We support, maintain and grow what we build — long after go-live.",
  },
];

/** Renders a quote with its `emphasis` part in bold coral. */
const Quote = ({ quote, emphasis }: { quote: string; emphasis?: string }) => {
  if (!emphasis || !quote.includes(emphasis)) return <>“{quote}”</>;
  const [before] = quote.split(emphasis);
  return (
    <>
      “{before}
      <strong>{emphasis}</strong>”
    </>
  );
};

const HomePage = () => {
  const [featured, secondary] = TESTIMONIALS;
  const stackRef = useRef<HTMLDivElement>(null);
  useStackedSections(stackRef);

  return (
    <>
    <Seo
      path="/"
      title="Software Development Company in Kitchener | Infinitude Stack"
      description="Websites, web apps and iOS & Android apps for businesses in Kitchener-Waterloo and across Canada. Design, development, cloud and support from one team."
      schema={[organizationSchema, websiteSchema]}
    />
    {/* Stacked sheets: each .is-stack__item pins once fully scrolled and the
        next one slides up over it (useStackedSections + motion.css). */}
    <div className="is-home is-stack" ref={stackRef}>
      {/* ---------- Hero ---------- */}
      <section id="start" className="is-container is-home-hero is-stack__item">
        <div className="is-home-hero__copy">
          <h1 className="is-display is-h1 is-home-hero__title">
            We build software that scales <strong className="is-accent-text">without limits.</strong>
          </h1>
          <p className="is-lede is-home-hero__lede">
            Strategy, design and engineering under one roof — from the first
            prototype to production at scale. One partner for the whole stack.
          </p>
          <div className="is-home-hero__actions">
            <Link to="/contact" className="is-btn is-btn--primary">
              Start a project →
            </Link>
            <Link to="/services" className="is-btn is-btn--outline">
              Explore services
            </Link>
          </div>
        </div>
        {/* The hero holds still; the next sheet slides up over it. */}
        <div className="is-home-hero__visual">
          <HeroLoop />
        </div>
      </section>

      <div id="services" className="is-stack__item is-stack__sheet is-home-first-sheet">

      {/* ---------- Promises ---------- */}
      <section className="is-container is-home-promises is-reveal" aria-label="Our promises">
        <StationTrack items={PROMISES} />
      </section>

      {/* ---------- Services ---------- */}
      <section className="is-container is-home-services" aria-labelledby="home-services-title">
        <div className="is-section-head is-reveal">
          <div>
            <p className="is-eyebrow">What we do</p>
            <h2 id="home-services-title" className="is-display is-h2">
              Seven services. <strong>One team.</strong>
            </h2>
          </div>
          <Link to="/services" className="is-underline-link">
            All services →
          </Link>
        </div>

        <ul className="is-index">
          {SERVICES.map((s, i) => (
            <li key={s.id} className="is-reveal">
              <Link to={`/services#${s.id}`} className="is-index__row">
                <span className="is-index__num">{String(i + 1).padStart(2, "0")}</span>
                <span className="is-index__name">{s.name}</span>
                <span className="is-index__text">{s.short}</span>
                <span className="is-index__arrow" aria-hidden="true">
                  <ArrowIcon size={16} />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <Link to="/contact" className="is-scope-bar">
          <span className="is-scope-bar__text">
            Not sure what you need? <span>We’ll help you scope it.</span>
          </span>
          <span className="is-scope-bar__cta">Book a free consultation →</span>
        </Link>
      </section>
      </div>

      {/* ---------- Clients ---------- */}
      <section id="clients" className="is-home-clients is-stack__item is-stack__sheet is-stack__sheet--dark" aria-labelledby="home-clients-title">
        <LoopWatermark />
        <div className="is-container is-section-head is-section-head--dark is-reveal">
          <div>
            <p className="is-eyebrow">Our clients</p>
            <h2 id="home-clients-title" className="is-display is-h2">
              Teams that <strong>build with us.</strong>
            </h2>
            <p className="is-home-clients__industries">{INDUSTRY_LINE}</p>
          </div>
          <Link to="/work" className="is-underline-link is-underline-link--light">
            See our work →
          </Link>
        </div>

        <Marquee className="is-home-clients__band" duration={40}>
          {CLIENTS.map((c) => (
            <Fragment key={c.name}>
              <span className={"is-home-clients__name" + (c.style ? ` is-home-clients__name--${c.style}` : "")}>
                {c.name}
              </span>
              <LogoMark size={44} stroke="var(--is-border-on-dark-strong)" strokeWidth={4.5} />
            </Fragment>
          ))}
        </Marquee>

        <div className="is-container is-home-quotes">
          <figure className="is-home-quote is-home-quote--main is-reveal">
            <LogoMark size={56} stroke="var(--is-text-on-dark)" strokeWidth={4} />
            <blockquote>
              <Quote quote={featured.quote} emphasis={featured.emphasis} />
            </blockquote>
            <figcaption>
              <span className="is-home-quote__line" />
              <span className="is-home-quote__author">{featured.author}</span>
              <span className="is-home-quote__role">{featured.role}</span>
            </figcaption>
          </figure>
          {secondary && (
            <figure className="is-home-quote is-home-quote--side is-reveal">
              <blockquote>
                <Quote quote={secondary.quote} emphasis={secondary.emphasis} />
              </blockquote>
              <figcaption>
                <span className="is-home-quote__line" />
                <span className="is-home-quote__author">{secondary.author}</span>
                <span className="is-home-quote__role">{secondary.role}</span>
              </figcaption>
            </figure>
          )}
        </div>
      </section>

      {/* ---------- Why us ---------- */}
      <div id="why" className="is-stack__item is-stack__sheet is-home-why-sheet">
      <section className="is-container is-home-why" aria-labelledby="home-why-title">
        <div className="is-home-why__head is-reveal">
          <p className="is-eyebrow">Why us</p>
          <h2 id="home-why-title" className="is-display is-h2">
            One partner. <strong className="is-accent-text">No hand-offs.</strong>
          </h2>
        </div>
        <div className="is-home-why__list is-reveal">
          <VerticalTrack items={WHY_US} />
        </div>
      </section>
      </div>

      {/* ---------- Technologies ---------- */}
      <section id="tech" className="is-home-tech is-stack__item is-stack__sheet" aria-labelledby="home-tech-title">
        <div className="is-container is-section-head is-reveal">
          <div>
            <p className="is-eyebrow">Technologies</p>
            <h2 id="home-tech-title" className="is-display is-h2">
              Modern tools, <strong>chosen for the job.</strong>
            </h2>
          </div>
          <Link to="/technologies" className="is-underline-link">
            Our full stack →
          </Link>
        </div>
        <div className="is-home-tech__rows">
          {TECH_MARQUEE.map((row, i) => (
            <Marquee key={i} duration={i === 0 ? 40 : 48} reverse={i === 1} gap={12}>
              {row.map((t) => (
                <span key={t.name} className={"is-tech-pill" + (t.tone ? ` is-tech-pill--${t.tone}` : "")}>
                  {t.name}
                </span>
              ))}
            </Marquee>
          ))}
        </div>
      </section>

      {/* ---------- Contact ---------- */}
      <div id="contact" className="is-stack__item is-stack__sheet">
        <HomeContact />
      </div>
    </div>
    </>
  );
};

export default HomePage;
